import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  root?: HTMLDivElement | null; // parent element, whose bounding rectangle will be considered the viewport
  rootMargin?: string; // top right bottom left "0px 0px 0px 0px"
  threshold?: number; // 0.0 -> 1.0 = 1.0 set to true when the entire object is visible
};
export const useElementAppears = ({
  root = null,
  rootMargin = '0px',
  threshold = 1,
}: Props) => {
  const refElement = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver>();

  const [state, setState] = useState<{
    visible: boolean;
    entry: IntersectionObserverEntry | null;
  }>({
    visible: false,
    entry: null,
  });

  const options = useMemo(
    () => ({ root, rootMargin, threshold }),
    [root, rootMargin, threshold],
  );

  const callbackObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setState({
          visible: true,
          entry,
        });
      }
    },
    [],
  );

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(callbackObserver, options);

    const { current: observerCurrent } = observer;

    if (refElement.current) observerCurrent.observe(refElement.current);

    return () => observerCurrent.disconnect();
  }, [options, callbackObserver]);

  return { ...state, refElement };
};
